import React, { createContext, useContext, useState, useEffect, useCallback } from "react"
import { supabase } from "../lib/supabase"
import { useAuth } from "./AuthContext"
import { searchAmazonProducts, generateAffiliateLink as createAffiliateLink } from "../services/amazonService"

const RecommendationContext = createContext()

export const useRecommendation = () => {
  const context = useContext(RecommendationContext)
  if (!context) {
    throw new Error("useRecommendation must be used within a RecommendationProvider")
  }
  return context
}

export const RecommendationProvider = ({ children }) => {
  const { user } = useAuth()
  const [preferences, setPreferences] = useState([])
  const [currentRecommendations, setCurrentRecommendations] = useState([])
  const [currentProducts, setCurrentProducts] = useState([])
  const [recommendationHistory, setRecommendationHistory] = useState([])
  const [recentProducts, setRecentProducts] = useState([]) // Track recent products to avoid duplicates
  const [recipients, setRecipients] = useState([])
  const [loading, setLoading] = useState(false)

  // Load data from localStorage on mount
  useEffect(() => {
    const loadStoredData = () => {
      try {
        const storageKey = user && !user.is_anonymous ? `user_${user.id}` : "guest"
        const currentVersion = "v2.0" // Updated version to clear old 8-product data

        // Check if we need to clear old data
        const storedVersion = localStorage.getItem(`version_${storageKey}`)
        if (storedVersion !== currentVersion) {
          console.log("Clearing old localStorage data due to version change")
          // Clear all old data
          localStorage.removeItem(`recommendations_${storageKey}`)
          localStorage.removeItem(`products_${storageKey}`)
          localStorage.removeItem(`recommendation_history_${storageKey}`)
          localStorage.removeItem(`recent_products_${storageKey}`)
          // Set new version
          localStorage.setItem(`version_${storageKey}`, currentVersion)
          return // Don't load any old data
        }

        // Load recommendations
        const storedRecommendations = localStorage.getItem(`recommendations_${storageKey}`)
        if (storedRecommendations) {
          setCurrentRecommendations(JSON.parse(storedRecommendations))
        }

        // Load products
        const storedProducts = localStorage.getItem(`products_${storageKey}`)
        if (storedProducts) {
          setCurrentProducts(JSON.parse(storedProducts))
        }

        // Load recommendation history
        const storedHistory = localStorage.getItem(`recommendation_history_${storageKey}`)
        if (storedHistory) {
          setRecommendationHistory(JSON.parse(storedHistory))
        }

        // Load recent products
        const storedRecentProducts = localStorage.getItem(`recent_products_${storageKey}`)
        if (storedRecentProducts) {
          setRecentProducts(JSON.parse(storedRecentProducts))
        }

        // Load preferences (already handled in existing code)
        const storedPreferences = localStorage.getItem(`${storageKey}_preferences`)
        if (storedPreferences) {
          setPreferences(JSON.parse(storedPreferences))
        }
      } catch (error) {
        console.error("Error loading stored data:", error)
      }
    }

    loadStoredData()
  }, [user])

  const saveToStorage = (key, data) => {
    try {
      const storageKey = user && !user.is_anonymous ? `user_${user.id}` : "guest"
      localStorage.setItem(`${key}_${storageKey}`, JSON.stringify(data))
    } catch (error) {
      console.error("Error saving to storage:", error)
    }
  }

  const savePreferences = async newPreferences => {
    try {
      setLoading(true)

      console.log("savePreferences called with:", { user, newPreferences })

      if (!user) {
        // For guest users, just store locally
        console.log("Saving preferences for guest user to localStorage")
        setPreferences(newPreferences)
        localStorage.setItem("guest_preferences", JSON.stringify(newPreferences))
        return { success: true }
      }

      if (user.is_anonymous) {
        // For anonymous users, also store locally
        console.log("Saving preferences for anonymous user to localStorage")
        setPreferences(newPreferences)
        localStorage.setItem("guest_preferences", JSON.stringify(newPreferences))
        return { success: true }
      }

      // For authenticated users, save to Supabase
      // Map the preferences data to match our database schema
      const { error } = await supabase.from("user_preferences").upsert({
        user_id: user.id,
        interests: newPreferences.categories || [],
        budget_range: newPreferences.priceRanges?.[0] || null,
        occasion: newPreferences.occasions?.[0] || null,
        relationship: newPreferences.relationships?.[0] || null,
        updated_at: new Date().toISOString(),
      })

      if (error) throw error

      setPreferences(newPreferences)
      return { success: true }
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to save preferences",
      }
    } finally {
      setLoading(false)
    }
  }

  const loadPreferences = async () => {
    try {
      setLoading(true)

      if (!user) {
        // For guest users, load from localStorage
        const guestPrefs = localStorage.getItem("guest_preferences")
        if (guestPrefs) {
          const parsedPrefs = JSON.parse(guestPrefs)
          setPreferences(parsedPrefs)
        }
        return { success: true }
      }

      // For authenticated users, load from Supabase
      const { data, error } = await supabase.from("user_preferences").select("*").eq("user_id", user.id).single()

      if (error && error.code !== "PGRST116") throw error // PGRST116 = no rows found

      if (data) {
        setPreferences(data)
      }

      return { success: true }
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to load preferences",
      }
    } finally {
      setLoading(false)
    }
  }

  const generateRecommendations = async (recipientInfo, occasion, budget, selectedRecipient = null) => {
    try {
      setLoading(true)

      // Add aggressive randomization to prevent identical results
      const timestamp = Date.now()
      const randomSeed = Math.random().toString(36).substring(7)
      const randomCategories = ["Tech", "Home", "Books", "Fashion", "Sports", "Art", "Kitchen", "Health", "Games", "Music"]
      const shuffledCategories = randomCategories.sort(() => Math.random() - 0.5).slice(0, 4)

      // Get recent products to avoid duplicates
      const recentProductNames = recentProducts
        .slice(0, 10)
        .map(p => p.product || p.title)
        .filter(Boolean)

      // Get user's onboarding preferences to enhance recommendations
      const userCategories = preferences?.categories || []
      const userStyles = preferences?.styles || []
      const userPriceRanges = preferences?.priceRanges || []

      // Create prompt for OpenAI with strict budget enforcement, variety instructions, and user preferences
      const prompt = `Generate 4 UNIQUE and DIFFERENT product recommendations for:

Recipient: ${recipientInfo.age} year old ${recipientInfo.gender || "person"}
Relationship: ${recipientInfo.relationship}
Interests: ${recipientInfo.interests?.join(", ") || "general"}
Occasion: ${occasion || "general gift giving"}
Budget: ${budget ? `$${budget}` : "flexible"}

USER'S STYLE PREFERENCES:
${userCategories.length > 0 ? `- Preferred Categories: ${userCategories.join(", ")}` : ""}
${userStyles.length > 0 ? `- Style Preferences: ${userStyles.join(", ")}` : ""}
${userPriceRanges.length > 0 ? `- Typical Budget Ranges: ${userPriceRanges.join(", ")}` : ""}

CRITICAL BUDGET CONSTRAINT: ${
        budget
          ? `ABSOLUTELY NO PRODUCTS OVER $${budget}! Every single recommendation MUST cost LESS than $${budget}. If a product costs $${budget} or more, DO NOT include it. Maximum allowed price: $${
              budget - 1
            }.`
          : "Provide a mix of price points."
      }

VARIETY REQUIREMENTS:
- Each product must be from a DIFFERENT category
- Consider these category suggestions for variety: ${shuffledCategories.join(", ")}
${userCategories.length > 0 ? `- PRIORITIZE these user-preferred categories when possible: ${userCategories.join(", ")}` : ""}
${userStyles.length > 0 ? `- Match these style preferences: ${userStyles.join(", ")}` : ""}
- Make each recommendation distinctly different from the others
- Vary price points significantly within the budget
${recentProductNames.length > 0 ? `- DO NOT suggest any of these recently recommended products: ${recentProductNames.join(", ")}` : ""}
- Be creative and think of unique, specific products that haven't been suggested recently

Create 4 DIFFERENT product suggestions that would make great gifts. Each should be a specific product, not a category.

For each recommendation, provide:
1. Category (e.g., "Tech Gadget", "Home & Kitchen", "Books")
2. Specific product name (e.g., "Wireless Bluetooth Headphones", "Stainless Steel Coffee Mug", "The Seven Husbands of Evelyn Hugo")
3. Brief reasoning (why this specific product fits the person)
4. Precise search term for finding this exact product on Amazon
5. Estimated price range (must be under budget if specified)

Make sure each product is unique and different from the others. Vary the categories and price points within the budget.
Session ID: ${timestamp}_${randomSeed}

Format as JSON array with objects containing: category, product, reasoning, searchTerm, priceRange`

      let recommendations

      // Check if OpenAI API key is available

      if (process.env.REACT_APP_OPENAI_API_KEY) {
        try {
          // Call OpenAI API directly (in production, this should be done server-side)
          const openaiResponse = await fetch("https://api.openai.com/v1/chat/completions", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`,
            },
            body: JSON.stringify({
              model: "gpt-3.5-turbo",
              messages: [
                {
                  role: "system",
                  content:
                    "You are a helpful gift recommendation assistant. Always respond with valid JSON. Provide varied and creative suggestions, avoiding repetitive recommendations.",
                },
                {
                  role: "user",
                  content: prompt,
                },
              ],
              max_tokens: 1200,
              temperature: 0.9, // Higher temperature for more variety
              top_p: 0.95, // Add top_p for additional randomness
            }),
          })

          if (openaiResponse.ok) {
            const openaiData = await openaiResponse.json()
            const recommendationsText = openaiData.choices[0].message.content

            try {
              // Clean the response text - remove markdown code blocks if present
              let cleanedText = recommendationsText.trim()

              // Remove ```json and ``` markers if present
              if (cleanedText.startsWith("```json")) {
                cleanedText = cleanedText.replace(/^```json\s*/, "").replace(/\s*```$/, "")
              } else if (cleanedText.startsWith("```")) {
                cleanedText = cleanedText.replace(/^```\s*/, "").replace(/\s*```$/, "")
              }

              const parsed = JSON.parse(cleanedText)

              // Handle different response formats from OpenAI
              if (Array.isArray(parsed)) {
                recommendations = parsed
              } else if (parsed.giftRecommendations && Array.isArray(parsed.giftRecommendations)) {
                recommendations = parsed.giftRecommendations
              } else if (parsed.recommendations && Array.isArray(parsed.recommendations)) {
                recommendations = parsed.recommendations
              } else {
                console.warn("Unexpected OpenAI response format:", parsed)
                recommendations = null
              }

              // Validate budget constraint - filter out any products over budget
              if (recommendations && budget) {
                const originalCount = recommendations.length
                recommendations = recommendations.filter(product => {
                  // Extract max price from priceRange (e.g., "$30 - $50" -> 50)
                  const priceRange = product.priceRange || ""
                  const priceMatch = priceRange.match(/\$(\d+)\s*-\s*\$(\d+)/)
                  if (priceMatch) {
                    const maxPrice = parseFloat(priceMatch[2])
                    const isValid = maxPrice <= budget
                    if (!isValid) {
                      console.warn(`Filtered out product "${product.product}" - max price $${maxPrice} exceeds budget $${budget}`)
                    }
                    return isValid
                  }
                  // If we can't parse the price range, keep the product
                  return true
                })
                console.log(`Budget validation: ${originalCount} -> ${recommendations.length} products`)
              }
            } catch (parseError) {
              console.error("Failed to parse OpenAI response:", parseError)
              recommendations = null
            }
          } else {
            const errorText = await openaiResponse.text()
            console.error("OpenAI API call failed:", openaiResponse.status, errorText)
            recommendations = null
          }
        } catch (openaiError) {
          console.error("OpenAI API error:", openaiError)
          recommendations = null
        }
      } else {
        console.warn("OpenAI API key not configured, using fallback recommendations")
        recommendations = null
      }

      // If OpenAI fails or is not configured, return error instead of fallbacks
      if (!recommendations) {
        throw new Error("AI recommendation service is currently unavailable. Please try again later.")
      }

      // Save session to Supabase if user is authenticated
      const sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

      if (user && !user.is_anonymous) {
        await supabase.from("recommendation_sessions").insert({
          user_id: user.id,
          recipient_id: selectedRecipient?.id || null,
          recipient_name: selectedRecipient?.name || null,
          session_data: JSON.stringify({ recipientInfo, occasion, budget }),
          recommendations: JSON.stringify(recommendations),
          occasion: occasion,
          budget: budget,
        })
      }

      setCurrentRecommendations(recommendations)

      // Track recent products to avoid duplicates in future requests
      const newRecentProducts = [...recommendations, ...recentProducts].slice(0, 20) // Keep last 20
      setRecentProducts(newRecentProducts)
      saveToStorage("recent_products", newRecentProducts)

      // Save recommendations to localStorage
      saveToStorage("recommendations", recommendations)

      // Save to history
      const historyEntry = {
        id: Date.now(),
        timestamp: new Date().toISOString(),
        recipient: recipientInfo,
        occasion,
        budget,
        recommendations,
        sessionId,
      }

      const newHistory = [historyEntry, ...recommendationHistory].slice(0, 10) // Keep last 10
      setRecommendationHistory(newHistory)

      // Save history to localStorage
      saveToStorage("recommendation_history", newHistory)

      return { success: true, recommendations, sessionId }
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to generate recommendations",
      }
    } finally {
      setLoading(false)
    }
  }

  const searchProducts = async (searchTerm, maxResults = 10) => {
    try {
      setLoading(true)
      console.log("RecommendationContext searchProducts called with:", searchTerm, maxResults)

      const result = await searchAmazonProducts(searchTerm, {
        limit: maxResults,
        country: "US",
      })

      console.log("searchAmazonProducts result:", result)

      // Save products to localStorage if successful
      if (result.success && result.products) {
        // Get existing products and add new ones
        const existingProducts = currentProducts || []
        const newProducts = [...existingProducts, ...result.products]
        setCurrentProducts(newProducts)
        saveToStorage("products", newProducts)
      }

      return result
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to search products",
      }
    } finally {
      setLoading(false)
    }
  }

  const getProductDetails = async productId => {
    try {
      setLoading(true)
      // Use our Amazon service to get product details
      const result = await import("../services/amazonService").then(module => module.getProductDetails(productId))
      return result
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to get product details",
      }
    } finally {
      setLoading(false)
    }
  }

  const generateAffiliateLink = async (productId, amazonUrl) => {
    console.log("generateAffiliateLink", productId, amazonUrl)
    try {
      if (!productId) {
        throw new Error("Product ID (ASIN) is required for affiliate link generation")
      }

      // Use our Amazon service to generate affiliate link
      const affiliateLink = createAffiliateLink(productId)
      console.log("Generated affiliate link:", affiliateLink)
      return { success: true, affiliateLink }
    } catch (error) {
      console.error("Affiliate link generation error:", error)
      return {
        success: false,
        error: error.message || "Failed to generate affiliate link",
      }
    }
  }

  const savePurchase = async purchaseData => {
    try {
      if (!user || user.is_anonymous) {
        // For guest users, just store locally
        const purchases = JSON.parse(localStorage.getItem("guest_purchases") || "[]")
        purchases.push({ ...purchaseData, timestamp: new Date().toISOString() })
        localStorage.setItem("guest_purchases", JSON.stringify(purchases))
        return { success: true }
      }

      // For authenticated users, save to Supabase
      const { error } = await supabase.from("purchase_history").insert({
        user_id: user.id,
        recipient_id: purchaseData.recipient_id || null,
        recipient_name: purchaseData.recipient_name || null,
        product_name: purchaseData.product_name,
        product_url: purchaseData.amazon_url,
        price: purchaseData.product_price,
        amazon_asin: purchaseData.product_id,
        affiliate_link: purchaseData.affiliate_link,
        recipient_info: purchaseData.recipient_info, // Legacy field
      })

      if (error) throw error
      return { success: true }
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to save purchase",
      }
    }
  }

  const loadRecommendationHistory = async () => {
    try {
      if (!user || user.is_anonymous) {
        // For guest users, load from localStorage
        const history = JSON.parse(localStorage.getItem("guest_recommendation_history") || "[]")
        setRecommendationHistory(history)
        return { success: true }
      }

      // For authenticated users, load from Supabase
      const { data, error } = await supabase
        .from("recommendation_sessions")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })

      if (error) throw error

      setRecommendationHistory(data || [])
      return { success: true }
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to load recommendation history",
      }
    }
  }

  // Recipient management functions
  const saveRecipient = async recipientData => {
    try {
      setLoading(true)

      if (!user || user.is_anonymous) {
        // For guest users, store locally
        const newRecipient = {
          id: `local_${Date.now()}`,
          ...recipientData,
          created_at: new Date().toISOString(),
        }
        const updatedRecipients = [...recipients, newRecipient]
        setRecipients(updatedRecipients)
        localStorage.setItem("guest_recipients", JSON.stringify(updatedRecipients))
        return { success: true, recipient: newRecipient }
      }

      // For authenticated users, save to Supabase
      const { data, error } = await supabase
        .from("recipient_profiles")
        .insert({
          user_id: user.id,
          ...recipientData,
        })
        .select()
        .single()

      if (error) throw error

      const updatedRecipients = [...recipients, data]
      setRecipients(updatedRecipients)
      return { success: true, recipient: data }
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to save recipient",
      }
    } finally {
      setLoading(false)
    }
  }

  const loadRecipients = async () => {
    try {
      if (!user || user.is_anonymous) {
        // For guest users, load from localStorage
        const guestRecipients = localStorage.getItem("guest_recipients")
        if (guestRecipients) {
          const parsed = JSON.parse(guestRecipients)
          setRecipients(parsed)
        } else {
          setRecipients([])
        }
        return { success: true }
      }

      // For authenticated users, load from Supabase
      const { data, error } = await supabase.from("recipient_profiles").select("*").eq("user_id", user.id).order("created_at", { ascending: false })

      if (error) throw error

      setRecipients(data || [])
      return { success: true }
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to load recipients",
      }
    }
  }

  const updateRecipient = async (recipientId, updates) => {
    try {
      setLoading(true)

      if (!user || user.is_anonymous) {
        // For guest users, update locally
        const updatedRecipients = recipients.map(r => (r.id === recipientId ? { ...r, ...updates, updated_at: new Date().toISOString() } : r))
        setRecipients(updatedRecipients)
        localStorage.setItem("guest_recipients", JSON.stringify(updatedRecipients))
        return { success: true }
      }

      // For authenticated users, update in Supabase
      const { error } = await supabase
        .from("recipient_profiles")
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq("id", recipientId)
        .eq("user_id", user.id)

      if (error) throw error

      const updatedRecipients = recipients.map(r => (r.id === recipientId ? { ...r, ...updates } : r))
      setRecipients(updatedRecipients)
      return { success: true }
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to update recipient",
      }
    } finally {
      setLoading(false)
    }
  }

  const deleteRecipient = async recipientId => {
    try {
      setLoading(true)

      if (!user || user.is_anonymous) {
        // For guest users, delete locally
        const updatedRecipients = recipients.filter(r => r.id !== recipientId)
        setRecipients(updatedRecipients)
        localStorage.setItem("guest_recipients", JSON.stringify(updatedRecipients))
        return { success: true }
      }

      // For authenticated users, delete from Supabase
      const { error } = await supabase.from("recipient_profiles").delete().eq("id", recipientId).eq("user_id", user.id)

      if (error) throw error

      const updatedRecipients = recipients.filter(r => r.id !== recipientId)
      setRecipients(updatedRecipients)
      return { success: true }
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to delete recipient",
      }
    } finally {
      setLoading(false)
    }
  }

  // Clear stored data (useful for testing or user logout)
  const clearStoredData = useCallback(() => {
    try {
      const storageKey = user && !user.is_anonymous ? `user_${user.id}` : "guest"
      localStorage.removeItem(`recommendations_${storageKey}`)
      localStorage.removeItem(`products_${storageKey}`)
      localStorage.removeItem(`recommendation_history_${storageKey}`)
      localStorage.removeItem(`recent_products_${storageKey}`)
      localStorage.removeItem(`version_${storageKey}`)
      localStorage.removeItem("guest_recipients")

      setCurrentRecommendations([])
      setCurrentProducts([])
      setRecommendationHistory([])
      setRecentProducts([])
      setRecipients([])
    } catch (error) {
      console.error("Error clearing stored data:", error)
    }
  }, [user])

  // Expose clear function globally for debugging
  useEffect(() => {
    window.clearGivvioData = () => {
      clearStoredData()
      console.log("Givvio localStorage data cleared!")
    }
    return () => {
      delete window.clearGivvioData
    }
  }, [clearStoredData])

  const value = {
    preferences,
    currentRecommendations,
    currentProducts,
    setCurrentProducts,
    recommendationHistory,
    recipients,
    loading,
    savePreferences,
    loadPreferences,
    generateRecommendations,
    searchProducts,
    getProductDetails,
    generateAffiliateLink,
    savePurchase,
    loadRecommendationHistory,
    clearStoredData,
    // Recipient management
    saveRecipient,
    loadRecipients,
    updateRecipient,
    deleteRecipient,
  }

  return <RecommendationContext.Provider value={value}>{children}</RecommendationContext.Provider>
}
